import React from 'react'
import { Plus, Trash2 } from 'lucide-react'

// Sửa nhanh các khối câu hỏi trong trình biên tập bài giảng (Bước 3b).
const Q_TYPES = new Set(['interactive_question', 'multiple_response_question', 'true_false', 'short_answer', 'sequence_question', 'quiz'])
export const isQuestionBlock = (t: string) => Q_TYPES.has(t)

const inp = 'w-full p-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-200 outline-none'
const lbl = 'text-[11px] font-bold text-gray-500'

const QuestionEditor: React.FC<{ block: any; onChange: (b: any) => void }> = ({ block, onChange }) => {
  const t = block?.type
  const set = (p: any) => onChange({ ...block, ...p })

  if (t === 'interactive_question' || t === 'multiple_response_question') {
    const opts: string[] = Array.isArray(block.options) ? block.options : []
    const multi = t === 'multiple_response_question'
    const corr: number[] = multi ? (Array.isArray(block.correctAnswers) ? block.correctAnswers : []) : []
    const setOpt = (idx: number, v: string) => { const o = [...opts]; o[idx] = v; set({ options: o }) }
    const toggle = (idx: number) => {
      if (multi) { const s = new Set(corr); s.has(idx) ? s.delete(idx) : s.add(idx); set({ correctAnswers: [...s].sort((a, b) => a - b) }) }
      else set({ correctAnswer: idx })
    }
    return (
      <div className="space-y-1.5">
        <label className={lbl}>Câu hỏi</label>
        <textarea className={inp} value={block.question || ''} onChange={(e) => set({ question: e.target.value })} />
        <label className={lbl}>Phương án ({multi ? 'tick nhiều đáp án đúng' : 'chọn 1 đáp án đúng'})</label>
        {opts.map((op, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <input type={multi ? 'checkbox' : 'radio'} checked={multi ? corr.includes(idx) : block.correctAnswer === idx} onChange={() => toggle(idx)} />
            <input className={inp} value={op} onChange={(e) => setOpt(idx, e.target.value)} />
            <button onClick={() => set({ options: opts.filter((_, k) => k !== idx) })} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button onClick={() => set({ options: [...opts, ''] })} className="text-xs text-blue-600 font-semibold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Thêm phương án</button>
        <textarea className={inp} value={block.explanation || ''} onChange={(e) => set({ explanation: e.target.value })} placeholder="Giải thích" />
        <input className={inp} value={block.hint || ''} onChange={(e) => set({ hint: e.target.value })} placeholder="Gợi ý (tuỳ chọn)" />
      </div>
    )
  }

  if (t === 'true_false') {
    return (
      <div className="space-y-1.5">
        <textarea className={inp} value={block.question || ''} onChange={(e) => set({ question: e.target.value })} placeholder="Mệnh đề" />
        <label className={`${lbl} flex items-center gap-2`}>Đáp án:
          <select className="border rounded p-1 text-sm" value={block.correct ? 'true' : 'false'} onChange={(e) => set({ correct: e.target.value === 'true' })}>
            <option value="true">Đúng</option><option value="false">Sai</option>
          </select>
        </label>
        <textarea className={inp} value={block.explanation || ''} onChange={(e) => set({ explanation: e.target.value })} placeholder="Giải thích" />
      </div>
    )
  }

  if (t === 'short_answer') {
    return (
      <div className="space-y-1.5">
        <textarea className={inp} value={block.question || ''} onChange={(e) => set({ question: e.target.value })} placeholder="Câu hỏi" />
        <input className={inp} value={block.answer || ''} onChange={(e) => set({ answer: e.target.value })} placeholder="Đáp án đúng" />
        <textarea className={inp} value={block.explanation || ''} onChange={(e) => set({ explanation: e.target.value })} placeholder="Giải thích" />
      </div>
    )
  }

  if (t === 'sequence_question') {
    const its: string[] = Array.isArray(block.items) ? block.items : []
    const sync = (a: string[]) => set({ items: a, correctSequence: a.map((_, k) => k) }) // lưu items theo ĐÚNG thứ tự
    return (
      <div className="space-y-1.5">
        <textarea className={inp} value={block.question || ''} onChange={(e) => set({ question: e.target.value })} placeholder="Yêu cầu sắp xếp" />
        <p className="text-[11px] text-gray-400">Các mục theo ĐÚNG thứ tự:</p>
        {its.map((it, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span className="text-xs w-5 text-gray-400">{idx + 1}.</span>
            <input className={inp} value={it} onChange={(e) => { const a = [...its]; a[idx] = e.target.value; sync(a) }} />
            <button onClick={() => sync(its.filter((_, k) => k !== idx))} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button onClick={() => sync([...its, ''])} className="text-xs text-blue-600 font-semibold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Thêm mục</button>
      </div>
    )
  }

  if (t === 'quiz') {
    const qs: any[] = Array.isArray(block.questions) ? block.questions : []
    const setQ = (idx: number, nq: any) => { const a = [...qs]; a[idx] = nq; set({ questions: a }) }
    const changeType = (idx: number, q: any, nt: string) => {
      const base = { question: q.question || '', explanation: q.explanation || '', points: q.points ?? (nt === 'true_false' ? 1 : 0.25) }
      const nq = nt === 'multiple_choice' ? { type: nt, ...base, options: q.options || ['', '', '', ''], correct: q.correct ?? 0 }
        : nt === 'true_false' ? { type: nt, ...base, correct: typeof q.correct === 'boolean' ? q.correct : true }
          : { type: nt, ...base, answer: q.answer || '', alternatives: q.alternatives || [] }
      setQ(idx, nq)
    }
    return (
      <div className="space-y-2">
        <input className={inp} value={block.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Tiêu đề bộ câu hỏi" />
        {qs.map((q, idx) => (
          <div key={idx} className="rounded border border-gray-200 p-2 space-y-1.5 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <select className="border rounded p-1 text-xs" value={q.type} onChange={(e) => changeType(idx, q, e.target.value)}>
                <option value="multiple_choice">Trắc nghiệm</option><option value="true_false">Đúng/Sai</option><option value="fill_blank">Trả lời ngắn</option>
              </select>
              <span className="text-[11px] text-gray-400 flex-1">Câu {idx + 1}</span>
              <button onClick={() => set({ questions: qs.filter((_, k) => k !== idx) })} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <textarea className={inp} value={q.question || ''} onChange={(e) => setQ(idx, { ...q, question: e.target.value })} placeholder="Câu hỏi" />
            {q.type === 'multiple_choice' && (q.options || ['', '', '', '']).map((op: string, oi: number) => (
              <div key={oi} className="flex items-center gap-1.5">
                <input type="radio" checked={q.correct === oi} onChange={() => setQ(idx, { ...q, correct: oi })} />
                <input className={inp} value={op} onChange={(e) => { const o = [...(q.options || [])]; o[oi] = e.target.value; setQ(idx, { ...q, options: o }) }} />
              </div>
            ))}
            {q.type === 'true_false' && (
              <select className="border rounded p-1 text-sm" value={q.correct ? 'true' : 'false'} onChange={(e) => setQ(idx, { ...q, correct: e.target.value === 'true' })}>
                <option value="true">Đúng</option><option value="false">Sai</option>
              </select>
            )}
            {q.type === 'fill_blank' && (
              <input className={inp} value={q.answer || ''} onChange={(e) => setQ(idx, { ...q, answer: e.target.value })} placeholder="Đáp án" />
            )}
            <textarea className={inp} value={q.explanation || ''} onChange={(e) => setQ(idx, { ...q, explanation: e.target.value })} placeholder="Giải thích" />
          </div>
        ))}
        <button onClick={() => set({ questions: [...qs, { type: 'multiple_choice', question: '', options: ['', '', '', ''], correct: 0, explanation: '', points: 0.25 }] })} className="text-xs text-blue-600 font-semibold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Thêm câu hỏi</button>
      </div>
    )
  }

  return <p className="text-xs text-gray-400">Loại này chưa hỗ trợ sửa nhanh.</p>
}
export default QuestionEditor
